import { create } from "zustand"

// Interfaces
export interface SecurityScore {
  name: string
  value: number
  color: string
}

export interface SecurityCategory {
  id: string
  name: string
  findings: number
  criticalCount: number
  status: "good" | "warning" | "critical"
  description: string
}

export interface SecurityIssue {
  id: string
  title: string
  severity: "critical" | "high" | "medium" | "low"
  resource: string
  description: string
  recommendation: string
  impact: string
  suppressed?: boolean
}

export interface SecurityFindings {
  overallScore: number
  totalFindings: number
  critical: number
  high: number
  medium: number
  low: number
  categories: SecurityCategory[]
  criticalIssues: SecurityIssue[]
}

// Store Interface
interface SecurityAdvisorStore {
  // State
  securityScore: SecurityScore[]
  securityFindings: SecurityFindings
  isLoading: boolean
  error: string | null

  // Actions
  fetchSecurityScore: () => Promise<void>
  fetchSecurityFindings: () => Promise<void>
  suppressIssue: (id: string) => void
  remediateIssue: (id: string) => Promise<void>
  addCategory: (category: Omit<SecurityCategory, "id">) => void
  updateCategory: (id: string, updates: Partial<SecurityCategory>) => void
  reset: () => void
}

// Mock Data
const mockSecurityScore: SecurityScore[] = [
  { name: "Secure", value: 68, color: "#10B981" },
  { name: "Warnings", value: 22, color: "#F59E0B" },
  { name: "Critical", value: 10, color: "#EF4444" },
]

const mockSecurityFindings: SecurityFindings = {
  overallScore: 78,
  totalFindings: 45,
  critical: 5,
  high: 10,
  medium: 15,
  low: 15,
  categories: [
    {
      id: "1",
      name: "IAM & Access Management",
      findings: 12,
      criticalCount: 2,
      status: "warning",
      description: "Issues with user permissions and access policies",
    },
    {
      id: "2",
      name: "Network Security",
      findings: 8,
      criticalCount: 1,
      status: "warning",
      description: "Open ports and security group misconfigurations",
    },
    {
      id: "3",
      name: "Data Encryption",
      findings: 6,
      criticalCount: 1,
      status: "warning",
      description: "Unencrypted data at rest and in transit",
    },
    {
      id: "4",
      name: "Logging & Monitoring",
      findings: 10,
      criticalCount: 1,
      status: "warning",
      description: "Missing CloudTrail logs and monitoring configurations",
    },
    {
      id: "5",
      name: "Compliance",
      findings: 9,
      criticalCount: 0,
      status: "good",
      description: "Compliance posture with industry standards",
    },
  ],
  criticalIssues: [
    {
      id: "1",
      title: "Public S3 Buckets Detected",
      severity: "critical",
      resource: "s3://prod-data-bucket",
      description: "S3 bucket allows public read access exposing sensitive data",
      recommendation: "Enable bucket encryption and restrict public access",
      impact: "Data breach risk",
    },
    {
      id: "2",
      title: "Root Account Activity",
      severity: "critical",
      resource: "AWS Root Account",
      description: "Root account used for daily operations instead of IAM users",
      recommendation: "Create IAM users with appropriate permissions",
      impact: "Unauthorized access risk",
    },
    {
      id: "3",
      title: "MFA Not Enabled",
      severity: "high",
      resource: "IAM Users (8)",
      description: "Multiple IAM users without multi-factor authentication",
      recommendation: "Enable MFA for all privileged users",
      impact: "Account compromise risk",
    },
    {
      id: "4",
      title: "Unrestricted Security Group",
      severity: "high",
      resource: "sg-0a1b2c3d4e5f",
      description: "Security group allows all inbound traffic from 0.0.0.0/0",
      recommendation: "Restrict access to specific IP ranges",
      impact: "Unauthorized network access",
    },
    {
      id: "5",
      title: "Unencrypted EBS Volumes",
      severity: "high",
      resource: "5 EBS Volumes",
      description: "Production volumes without encryption at rest",
      recommendation: "Enable EBS volume encryption",
      impact: "Data exposure risk",
    },
  ],
}

// Initial State
const initialState = {
  securityScore: [],
  securityFindings: {
    overallScore: 0,
    totalFindings: 0,
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    categories: [],
    criticalIssues: [],
  },
  isLoading: false,
  error: null,
}

// Store
export const useSecurityAdvisorStore = create<SecurityAdvisorStore>((set) => ({
  ...initialState,

  // Actions
  fetchSecurityScore: async () => {
    set({ isLoading: true, error: null })
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 300))
      set({ securityScore: mockSecurityScore, isLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "Failed to fetch security score",
        isLoading: false,
      })
      throw error
    }
  },

  fetchSecurityFindings: async () => {
    set({ isLoading: true, error: null })
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 450))
      set({ securityFindings: mockSecurityFindings, isLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "Failed to fetch security findings",
        isLoading: false,
      })
      throw error
    }
  },

  suppressIssue: (id) => {
    set((state) => ({
      securityFindings: {
        ...state.securityFindings,
        criticalIssues: state.securityFindings.criticalIssues.map((issue) =>
          issue.id === id ? { ...issue, suppressed: true } : issue
        ),
      },
    }))
  },

  remediateIssue: async (id) => {
    try {
      // Simulate remediation API call
      await new Promise((resolve) => setTimeout(resolve, 800))
      set((state) => ({
        securityFindings: {
          ...state.securityFindings,
          criticalIssues: state.securityFindings.criticalIssues.filter((issue) => issue.id !== id),
        },
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "Failed to remediate issue",
      })
      throw error
    }
  },

  addCategory: (category) => {
    set((state) => ({
      securityFindings: {
        ...state.securityFindings,
        categories: [
          ...state.securityFindings.categories,
          {
            ...category,
            id: Date.now().toString(),
          },
        ],
      },
    }))
  },

  updateCategory: (id, updates) => {
    set((state) => ({
      securityFindings: {
        ...state.securityFindings,
        categories: state.securityFindings.categories.map((cat) => (cat.id === id ? { ...cat, ...updates } : cat)),
      },
    }))
  },

  reset: () => set(initialState),
}))

// Selectors
export const selectSecurityScore = (state: SecurityAdvisorStore) => state.securityScore
export const selectSecurityFindings = (state: SecurityAdvisorStore) => state.securityFindings
export const selectCriticalIssues = (state: SecurityAdvisorStore) =>
  state.securityFindings.criticalIssues.filter((issue) => issue.severity === "critical" && !issue.suppressed)
export const selectHighPriorityIssues = (state: SecurityAdvisorStore) =>
  state.securityFindings.criticalIssues.filter(
    (issue) => (issue.severity === "critical" || issue.severity === "high") && !issue.suppressed
  )
export const selectCategoriesAtRisk = (state: SecurityAdvisorStore) =>
  state.securityFindings.categories.filter((cat) => cat.status === "warning" || cat.status === "critical")
